import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  MultiCountryFeasCpiData,
  MultiCountryRunFeasibilityInfo,
} from '../../shared/ui/multicountry/multi-country-draft/multi-country-draft.interface';
import {
  McPreDraftSurvey,
  McSpvSurveyRequest,
  MultiCountrySpvSurvey,
  MultiCountrySurvey,
} from '../../shared/ui/multicountry/interface/multi-country.interface';
import { BuyerSurveyResponse } from '../../../shared/interfaces/survey.interface';
import {
  McSvpLocaleRemoverData,
  McSvpSetterData,
} from '../../shared/ui/multicountry/domain/mc-svp-survey-payload-composer';
import {
  McTraditionalSetterData,
  McTraditionalSettings,
} from '../../shared/ui/multicountry/domain/mc-traditional-survey-settings-composer';

@Injectable({
  providedIn: 'root',
})
export class MultiCountryDraftService {
  private _mcFeasCpiDefaultValue = {} as MultiCountryFeasCpiData;
  private _mcFeasCpiData$ = new BehaviorSubject<MultiCountryFeasCpiData>(
    this._mcFeasCpiDefaultValue
  );
  private _mcRunFeasibilityInfoDefault = <MultiCountryRunFeasibilityInfo>{};
  private _mcRunFeasibilityInfo$ =
    new BehaviorSubject<MultiCountryRunFeasibilityInfo>(
      this._mcRunFeasibilityInfoDefault
    );
  private _mcPreDraftData$ = new BehaviorSubject<McPreDraftSurvey[]>([]);
  private _mcSurveyData$ = new Subject<MultiCountrySurvey[]>();
  private _mcSpvSurveysResponse$ = new Subject<BuyerSurveyResponse[]>();
  private _mcSpvUpdatedSurveysResponse$ = new BehaviorSubject<
    BuyerSurveyResponse[]
  >([]);
  private _mcSpvGoalChanged$ = new Subject<number>();
  private _mcSpvSurveyPayloadCacheCommand$ =
    new EventEmitter<McSvpSetterData>();
  private _mcSpvSurveyRequest$ = new BehaviorSubject(<McSpvSurveyRequest>{});
  private _mcSvpSurveyRemoveFromCacheCommand$ =
    new EventEmitter<McSvpLocaleRemoverData>();
  private _mcSpvSelectedChildId$ = new BehaviorSubject<number | null>(null);
  private _mcTraditionalSurveysResponse$ = new Subject<BuyerSurveyResponse[]>();
  private _mcSpvChildSurveys$ = new Subject<MultiCountrySpvSurvey[]>();
  private _mcTraditionalSettingsCacheCommand$ = new EventEmitter<
    McTraditionalSetterData[]
  >();
  private _mcTraditionalSettingsResetCommand$ = new EventEmitter<void>();
  private _mcTraditionalSettingsRemoveCommand$ = new Subject<number>();
  private _mcTraditionalSettings$ = new BehaviorSubject<
    McTraditionalSettings[]
  >([]);
  private _AfterSaveAllocationMcSpvChildId$ = new BehaviorSubject<
    number | null
  >(null);

  private _mcTraditionalSurveyRequestPayload$ = new BehaviorSubject<{
    [surveyId: number]: BuyerSurveyResponse;
  }>({});

  private _mcTraditionalUpdateCommand$ = new Subject<void>();

  private _shouldCallMcSvpFeasibilityFromFlyout$ = new EventEmitter<boolean>();

  constructor() {}

  setMultiCountryFeasCpiData(data: MultiCountryFeasCpiData) {
    this._mcFeasCpiData$.next(data);
  }

  getMultiCountryFeasCpiData(): Observable<MultiCountryFeasCpiData> {
    return this._mcFeasCpiData$.asObservable();
  }

  setMultiCountryRunFeasibilityInfo(data: MultiCountryRunFeasibilityInfo) {
    this._mcRunFeasibilityInfo$.next(data);
  }

  setMultiCountryRunFeasibilityInfoDefault() {
    this._mcRunFeasibilityInfo$.next(this._mcRunFeasibilityInfoDefault);
  }

  getMultiCountryRunFeasibilityInfo(): Observable<MultiCountryRunFeasibilityInfo> {
    return this._mcRunFeasibilityInfo$.asObservable();
  }

  setMultiCountryPreDraftData(data: McPreDraftSurvey[]) {
    this._mcPreDraftData$.next(data);
  }

  getMultiCountryPreDraftData(): Observable<McPreDraftSurvey[]> {
    return this._mcPreDraftData$.asObservable();
  }

  setMultiCountrySurveyData(data: MultiCountrySurvey[]) {
    this._mcSurveyData$.next(data);
  }

  getMultiCountrySurveyData(): Observable<MultiCountrySurvey[]> {
    return this._mcSurveyData$.asObservable();
  }

  sendSpvSurveyResponseData(surveys: BuyerSurveyResponse[]): void {
    this._mcSpvSurveysResponse$.next(surveys);
  }

  getSpvSurveyResponseData(): Observable<BuyerSurveyResponse[]> {
    return this._mcSpvSurveysResponse$.asObservable();
  }

  sendMcSpvUpdateInCacheCommand(data: McSvpSetterData) {
    this._mcSpvSurveyPayloadCacheCommand$.next(data);
  }

  getMcSpvUpdateInCacheCommand(): Observable<McSvpSetterData> {
    return this._mcSpvSurveyPayloadCacheCommand$.asObservable();
  }

  sendMcSpvSurveyRequest(data: McSpvSurveyRequest): void {
    this._mcSpvSurveyRequest$.next(data);
  }

  getMcSpvSurveyRequest(): Observable<McSpvSurveyRequest> {
    return this._mcSpvSurveyRequest$.asObservable();
  }

  setMcSpvSelectedChildId(id: number | null): void {
    this._mcSpvSelectedChildId$.next(id);
  }

  getMcSpvSelectedChildId(): Observable<number | null> {
    return this._mcSpvSelectedChildId$.asObservable();
  }

  sendMcTraditionalSurveysResponse(surveys: BuyerSurveyResponse[]): void {
    this._mcTraditionalSurveysResponse$.next(surveys);
  }

  getMcTraditionalSurveysResponse(): Observable<BuyerSurveyResponse[]> {
    return this._mcTraditionalSurveysResponse$.asObservable();
  }

  public sendMcSpvChildSurveys(data: MultiCountrySpvSurvey[]): void {
    this._mcSpvChildSurveys$.next(data);
  }

  public getMcSpvChildSurveys(): Observable<MultiCountrySpvSurvey[]> {
    return this._mcSpvChildSurveys$.asObservable();
  }

  public sendMcTraditionalUpdateInCache(data: McTraditionalSetterData[]): void {
    this._mcTraditionalSettingsCacheCommand$.next(data);
  }

  public getMcTraditionalUpdateInCache(): Observable<
    McTraditionalSetterData[]
  > {
    return this._mcTraditionalSettingsCacheCommand$.asObservable();
  }

  public sendMcTraditionalSettingsForReset(): void {
    this._mcTraditionalSettingsResetCommand$.next();
  }

  public getMcTraditionalSettingsForReset(): Observable<void> {
    return this._mcTraditionalSettingsResetCommand$.asObservable();
  }

  public sendMcTraditionalSettingsForRemoval(surveyId: number): void {
    this._mcTraditionalSettingsRemoveCommand$.next(surveyId);
  }

  public getMcTraditionalSettingsForRemoval(): Observable<number> {
    return this._mcTraditionalSettingsRemoveCommand$.asObservable();
  }

  public sendMcTraditionalSettings(data: McTraditionalSettings[]): void {
    this._mcTraditionalSettings$.next(data);
  }

  public getMcTraditionalSettings(): Observable<McTraditionalSettings[]> {
    return this._mcTraditionalSettings$.asObservable();
  }

  public sendMcSvpUpdatedSurveysResponse(data: BuyerSurveyResponse[]): void {
    this._mcSpvUpdatedSurveysResponse$.next(data);
  }

  public getMcSvpUpdatedSurveysResponse(): Observable<BuyerSurveyResponse[]> {
    return this._mcSpvUpdatedSurveysResponse$.asObservable();
  }

  setAfterSaveAllocationMcSpvChildId(id: number): void {
    this._AfterSaveAllocationMcSpvChildId$.next(id);
  }

  getAfterSaveAllocationMcSpvChildId(): Observable<number | null> {
    return this._AfterSaveAllocationMcSpvChildId$.asObservable();
  }

  public sendMcSpvRemoveFromCache(data: McSvpLocaleRemoverData): void {
    this._mcSvpSurveyRemoveFromCacheCommand$.next(data);
  }

  public getMcSpvRemoveFromCache(): Observable<McSvpLocaleRemoverData> {
    return this._mcSvpSurveyRemoveFromCacheCommand$.asObservable();
  }

  public getMcTraditionalSurveyRequest() {
    return this._mcTraditionalSurveyRequestPayload$.asObservable();
  }

  public sendMcTraditionalSurveyRequest(data: {
    [surveyId: number]: BuyerSurveyResponse;
  }): void {
    return this._mcTraditionalSurveyRequestPayload$.next(data);
  }

  public sendMcTraditionalUpdateCommand() {
    this._mcTraditionalUpdateCommand$.next();
  }

  public getMcTraditionalUpdateCommand() {
    return this._mcTraditionalUpdateCommand$.asObservable();
  }

  public sendShouldCallMcSvpFeasibilityFromFlyout(value: boolean): void {
    this._shouldCallMcSvpFeasibilityFromFlyout$.next(value);
  }

  public getShouldCallMcSvpFeasibilityFromFlyout(): Observable<boolean> {
    return this._shouldCallMcSvpFeasibilityFromFlyout$.asObservable();
  }

  public resetMultiCountryData(): void {
    // MC-QB survey data
    this.setMcSpvSelectedChildId(null);
    this.sendMcSvpUpdatedSurveysResponse([]);
    this.sendMcSpvSurveyRequest(<McSpvSurveyRequest>{});
  }

  public getMcSpvGoalChangedCommand() {
    return this._mcSpvGoalChanged$.asObservable();
  }

  public sendMcSpvGoalChangedCommand(goal: number) {
    return this._mcSpvGoalChanged$.next(goal);
  }
}
